import { useState, useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useNotifications } from '@/contexts/NotificationContext';
import { useReauth } from '@/contexts/ReauthContext';
import { useMcAccessStatus } from '@/hooks/useMcAccessStatus';
import { extractReauthError } from '@/lib/reauth';
import { useAsyncJob } from './useAsyncJob';
import i18n from '@/lib/i18n';

interface UploadJob {
  id: string;
  status: string;
  total_products: number;
  uploaded_products: number;
  failed_products: number;
  queued_batches: number;
  processed_batches: number;
  /**
   * Fehlergrund aus `upload_jobs.error_message` (z.B. der widerrufene
   * Google-Zugriff aus process-upload-queue). Der Completion-Nachfetch laedt
   * die volle Zeile (`select('*')`) — ohne dieses Feld kam der Grund im
   * Frontend nie an und der Fehlerdialog zeigte Zahlen ohne Ursache
   * (Konvergenz 6, Befund 3).
   */
  error_message?: string | null;
}

// Selection scope from the dashboard (AdvancedModePanel) — mirrors the
// optimization flow: explicit productIds when the user checked individual
// products, filters when select-all with active filters is on. Empty object /
// undefined → whole feed (legacy behavior).
export interface UploadFilters {
  search?: string;
  availability?: string;
  brand?: string;
  optimizationStatus?: string;
  approvalStatus?: string;
  // 'uploaded' → restrict to products already pushed to GMC (uploaded_to_gmc_at
  // IS NOT NULL), mirroring the "Uploaded" status pill. Any other value is ignored.
  uploadStatus?: string;
}

export interface UploadSelection {
  productIds?: string[];
  filters?: UploadFilters;
}

// Keep in sync with supabase/functions/_shared/upload-approval.ts — die
// Dialog-Zahlen muessen exakt vorhersagen, was der Server auswaehlt.
//
// Seit Task 11 waehlt der MANUELLE Pfad ausschliesslich
// `approval_status='approved'`. Jeder Aufruf von hier ist ein manueller Pfad:
// das Frontend traegt keinen Service-Role-Schluessel und kann den
// Automatik-Marker `triggered_by='auto-opt'` gar nicht setzen. Die frueher hier
// gespiegelte Drei-Zustands-Bedingung (approved | pending_review | NULL) gehoert
// jetzt allein dem Automatik-Pfad und darf hier nicht wieder auftauchen —
// sonst versprechen die Zahlen wieder mehr, als der Server hochlaedt.
const MANUAL_APPROVAL_STATUS = 'approved';
const ID_IN_CHUNK_SIZE = 200;

// Fehlercodes des Control-Guards aus enqueue-upload-batches. Beide heissen: es
// wurde NICHTS eingereiht. Ohne eigene Uebersetzung landen sie als roher
// Grossbuchstaben-Code im Toast, und der Nutzer haelt gewolltes Verhalten
// (Kontrollgruppe bleibt zurueck) fuer einen Defekt.
type UploadGuardCode = 'EXPERIMENT_CONTROL_BLOCKED' | 'CONTROL_GUARD_UNAVAILABLE';

function isUploadGuardCode(code: unknown): code is UploadGuardCode {
  return code === 'EXPERIMENT_CONTROL_BLOCKED' || code === 'CONTROL_GUARD_UNAVAILABLE';
}

interface EdgeErrorBody {
  error?: string;
  skipped_control?: number;
}

// Liest den JSON-Body einer non-2xx-Antwort. supabase-js legt die rohe Response
// unter error.context ab. Geklont wird, weil extractReauthError denselben Body
// gleich danach noch einmal liest und ein Response-Body nur einmal lesbar ist.
async function readEdgeErrorBody(res: { data: unknown; error: unknown }): Promise<EdgeErrorBody | null> {
  const ctx = (res.error as { context?: unknown } | null | undefined)?.context as
    | { json?: () => Promise<unknown>; clone?: () => { json?: () => Promise<unknown> } }
    | undefined;
  if (!ctx) return null;
  try {
    const source = typeof ctx.clone === 'function' ? ctx.clone() : ctx;
    const body = await source.json?.();
    return body && typeof body === 'object' ? (body as EdgeErrorBody) : null;
  } catch {
    // Body war kein JSON oder bereits gelesen, dann greift die generische Meldung.
    return null;
  }
}

function chunkIds(ids: string[], size: number): string[][] {
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += size) {
    chunks.push(ids.slice(i, i + size));
  }
  return chunks;
}

async function countUploadScope(
  feedId: string,
  selection: UploadSelection,
): Promise<number> {
  const f = selection.filters;
  // Provably-empty combinations (mirror enqueue-upload-batches):
  // upload requires optimization_status='completed'; ein Freigabe-Filter auf
  // irgendetwas ausser 'approved' kann die Pflicht-Freigabe nie treffen.
  if (f?.optimizationStatus && f.optimizationStatus !== 'completed') return 0;
  const approvalFilter = f?.approvalStatus;
  if (approvalFilter && approvalFilter !== MANUAL_APPROVAL_STATUS) return 0;

  const buildBase = () =>
    supabase
      .from('products')
      .select('id', { count: 'exact', head: true })
      .eq('feed_id', feedId)
      .eq('optimization_status', 'completed')
      .eq('approval_status', MANUAL_APPROVAL_STATUS);

  if (selection.productIds?.length) {
    let total = 0;
    for (const chunk of chunkIds(selection.productIds, ID_IN_CHUNK_SIZE)) {
      const { count, error } = await buildBase().in('id', chunk);
      if (error) throw error;
      total += count ?? 0;
    }
    return total;
  }

  let q = buildBase();
  if (f?.availability) q = q.eq('availability', f.availability);
  if (f?.brand) q = q.eq('brand', f.brand);
  if (f?.uploadStatus === 'uploaded') q = q.not('uploaded_to_gmc_at', 'is', null);
  if (f?.search) {
    const term = `%${f.search}%`;
    q = q.or(`title.ilike.${term},description.ilike.${term},brand.ilike.${term},optimized_title.ilike.${term},optimized_description.ilike.${term}`);
  }
  const { count, error } = await q;
  if (error) throw error;
  return count ?? 0;
}

export function useUploadJob(
  feedId: string | null,
  refetchStats: () => void,
  getUploadSelection?: () => UploadSelection,
) {
  const { toast } = useNotifications();
  const { triggerReauth } = useReauth();
  const { isAccessRevoked: isMcAccessRevoked, mcName: revokedMcName } = useMcAccessStatus();
  const queryClient = useQueryClient();

  const [activeUploadJob, setActiveUploadJob] = useState<UploadJob | null>(null);
  const [completedUploadJob, setCompletedUploadJob] = useState<UploadJob | null>(null);
  const [uploadSubmitting, setUploadSubmitting] = useState(false);
  const [cancellingUpload, setCancellingUpload] = useState(false);
  const [showUploadSuccessDialog, setShowUploadSuccessDialog] = useState(false);
  const [showUploadConfirmDialog, setShowUploadConfirmDialog] = useState(false);
  // Nicht-destruktiver Schliessweg fuer den Fortschrittsdialog: die Copy
  // verspricht "du kannst dieses Fenster sicher schliessen" — der Job laeuft
  // serverseitig weiter (useAsyncJob pollt ihn weiter). Zuruecksetzen beim
  // Start eines neuen Uploads und bei Completion, damit das Ergebnis wieder
  // sichtbar wird.
  const [uploadDialogDismissed, setUploadDialogDismissed] = useState(false);
  const [lastUploadStats, setLastUploadStats] = useState<{ uploaded: number; failed: number }>({ uploaded: 0, failed: 0 });
  // Active selection scope for the pending confirm dialog / upload run.
  // null → whole feed (legacy behavior, counts come from feed-wide stats).
  const [uploadSelection, setUploadSelection] = useState<UploadSelection | null>(null);
  // Nur noch EINE Zahl: der manuelle Pfad kennt seit Task 11 keine zweite
  // Menge mehr. Ein zweites Feld "all" waere eine Zahl ohne Handlung dahinter.
  const [scopedCounts, setScopedCounts] = useState<{ approved: number } | null>(null);
  const [countsLoading, setCountsLoading] = useState(false);
  const cancelledUploadRef = useRef(false);
  // Guards against a slow count query of a previous dialog-open overwriting a newer one.
  const countsRequestRef = useRef(0);
  // Herkunfts-Gate (Konvergenz 4, Befund 2, Muster startedHereRef aus
  // FeedHealthDashboard): upload_jobs ist realtime-publiziert — jeder Tab mit
  // offener Werkbank sieht JEDEN aktiven Upload dieses Feeds (zweiter Tab,
  // naechtlicher Auto-Upload mit triggered_by='auto-opt'). Nur Laeufe, die in
  // DIESER Hook-Instanz gestartet wurden, duerfen den fokusfangenden
  // Fortschrittsdialog und die Erfolgs-Zeremonie aufziehen; fremde Laeufe
  // bleiben ein unaufdringlicher Hinweis. Set statt Einzelwert, damit ein
  // schneller Folge-Upload den vorigen nicht vergisst.
  const startedJobIdsRef = useRef<Set<string>>(new Set());

  // Refs for state values read inside the completion effect (avoids stale closures)
  const activeUploadJobRef = useRef(activeUploadJob);
  activeUploadJobRef.current = activeUploadJob;
  const uploadSubmittingRef = useRef(uploadSubmitting);
  uploadSubmittingRef.current = uploadSubmitting;

  const {
    activeJob: uploadJobData,
    cancelJob,
    invalidate,
  } = useAsyncJob<UploadJob>({
    tableName: 'upload_jobs',
    channelName: 'simple-upload-job-updates',
    queryKeyPrefix: 'simple-upload-job',
    feedId,
    forcePolling: !!activeUploadJob,
  });

  // Sync query data to local state and handle completion
  useEffect(() => {
    if (uploadJobData) {
      setActiveUploadJob(uploadJobData);
    } else if (activeUploadJobRef.current && !uploadSubmittingRef.current) {
      const currentJob = activeUploadJobRef.current;
      if (currentJob.status === 'processing' || currentJob.status === 'queued') {
        supabase
          .from('upload_jobs')
          .select('*')
          .eq('id', currentJob.id)
          .single()
          .then(({ data }) => {
            if (data) {
              const finalJob = data as UploadJob;
              // Zeremonie (Ergebnis-Dialog -> Erfolgs-Dialog) nur fuer hier
              // gestartete Laeufe; fremde/automatische enden als stiller
              // Toast im else-Zweig (Befund 2: sonst wiederholen BEIDE Tabs
              // die volle Erfolgs-Zeremonie).
              const startedHere = startedJobIdsRef.current.has(finalJob.id);
              if (!cancelledUploadRef.current && startedHere && (finalJob.uploaded_products > 0 || finalJob.failed_products > 0)) {
                setLastUploadStats({ uploaded: finalJob.uploaded_products, failed: finalJob.failed_products });
                // Keep the dialog open so user can review the result and close manually.
                // Ein waehrend des Laufs weggeklickter Dialog kommt fuer das
                // Ergebnis zurueck — das Schliessen galt dem Warten, nicht dem
                // Resultat.
                setUploadDialogDismissed(false);
                setCompletedUploadJob(finalJob);
              } else {
                toast({
                  tone: finalJob.status === 'completed' && finalJob.failed_products === 0 ? 'success' : 'warning',
                  title: finalJob.status === 'completed' ? i18n.t('dashboard:uploadSuccess.title') : i18n.t('dashboard:uploadProgress.titleFailed'),
                  description: `${finalJob.uploaded_products} ${i18n.t('dashboard:uploadProgress.uploaded')}${finalJob.failed_products > 0 ? `, ${finalJob.failed_products} ${i18n.t('dashboard:uploadProgress.failed')}` : ''}.`,
                  variant: finalJob.uploaded_products > 0 ? 'default' : 'destructive',
                });
              }
              cancelledUploadRef.current = false;
            }
            setActiveUploadJob(null);
            refetchStats();
            // Refresh the FeedSwitcher tree checkmarks (feed/MC) — a finished
            // upload may have flipped the qualifier for this feed. Prefix-matches
            // useContextTree's TREE_KEY, so both the tree and its nested
            // 'uploaded' query refetch.
            queryClient.invalidateQueries({ queryKey: ['google-accounts', 'context-tree'] });
            // V2-Befund: die Produktzeilen der Werkbank wussten vom Upload
            // nichts. Die Statuspille — die Spalte, die Freigabe und
            // Auslieferung gerade erst getrennt hat — liest `upload_status`
            // direkt aus diesem Zwischenspeicher und behauptete danach weiter,
            // die soeben hochgeladenen Produkte seien nicht bei Google.
            //
            // Hier wird bewusst WIRKLICH nachgeladen (kein `refetchType: 'none'`
            // wie bei einer Einzelentscheidung): der Server hat `upload_status`
            // geaendert, und der Wert laesst sich clientseitig nicht ehrlich
            // fortschreiben — welche Produkte der Lauf tatsaechlich erreicht
            // hat, weiss nur er.
            queryClient.invalidateQueries({ queryKey: ['workbench-products', feedId] });
          });
      } else {
        setActiveUploadJob(null);
      }
    }
  }, [uploadJobData]);

  const isUploading = !!activeUploadJob || uploadSubmitting;

  const handleUploadButtonClick = () => {
    // Defense-in-depth — UI button is also disabled when MC access is revoked.
    if (isMcAccessRevoked) {
      toast({
        title: i18n.t('dashboard:toasts.mcRevokedTitle'),
        description: revokedMcName
          ? i18n.t('dashboard:toasts.uploadMcRevokedNamed', { name: revokedMcName })
          : i18n.t('dashboard:toasts.uploadMcRevoked'),
        variant: 'destructive',
      });
      return;
    }

    // Snapshot the dashboard selection at click time. Only treat it as a scope
    // when it actually narrows the feed — default state (select-all, no filters)
    // stays on the feed-wide path with stats-based counts.
    const raw = getUploadSelection?.();
    const selection: UploadSelection | null =
      raw && (raw.productIds?.length || (raw.filters && Object.keys(raw.filters).length > 0))
        ? raw
        : null;
    setUploadSelection(selection);
    setScopedCounts(null);

    if (selection && feedId) {
      const requestId = ++countsRequestRef.current;
      setCountsLoading(true);
      countUploadScope(feedId, selection)
        .then((approved) => {
          if (countsRequestRef.current !== requestId) return;
          setScopedCounts({ approved });
        })
        .catch((error) => {
          if (countsRequestRef.current !== requestId) return;
          Sentry.captureException(error, { tags: { feature: 'upload', action: 'count-scope' } });
          // Without accurate counts the dialog can't promise a scoped upload —
          // close it instead of silently falling back to "whole feed".
          setShowUploadConfirmDialog(false);
          setUploadSelection(null);
          toast({
            title: i18n.t('dashboard:toasts.uploadFailed'),
            description: error instanceof Error ? error.message : i18n.t('dashboard:toasts.uploadError'),
            variant: 'destructive',
          });
        })
        .finally(() => {
          if (countsRequestRef.current !== requestId) return;
          setCountsLoading(false);
        });
    } else {
      setCountsLoading(false);
    }

    setShowUploadConfirmDialog(true);
  };

  // Upload handler.
  //
  // Rueckgabe (N7): Anzahl der vom Server EINGEREIHTEN Produkte, oder `null`,
  // wenn nichts eingereiht wurde (kein Feed, widerrufener Merchant-Center-
  // Zugriff, Control-Guard, Reauth noetig, Fehler, bereits laufender Submit).
  // Aufrufer, die den Upload melden wollen, brauchen genau diese
  // Unterscheidung: ein Klick ist kein Upload, und die erwartete Zahl ist
  // nicht die eingereihte — der Control-Guard eines laufenden Experiments
  // haelt Produkte zurueck.
  //
  // Doppelklick-Schutz (2.6): Radix haelt den Bestaetigungsdialog fuer die
  // 200-ms-Ausblend-Animation gemountet und klickbar — ein zweiter Klick in
  // diesem Fenster startete einen ZWEITEN upload_job mit identischem Body,
  // und "Abbrechen" im Fortschrittsdialog erreichte nur den neueren
  // (useAsyncJob nimmt `order(created_at desc).limit(1)`); Job 1 schrieb
  // unsichtbar weiter ins Merchant Center. Ein Ref statt (nur) State, weil
  // der State-Guard erst nach dem naechsten Render greift — dieselbe
  // Begruendung wie reviewActionInFlight in WorkbenchReviewer.tsx. Der
  // `loading`-Knopf im Dialog ist die sichtbare Haelfte dieses Schutzes.
  const uploadInFlightRef = useRef(false);

  const handleUpload = async (approvedOnly: boolean = false, addCustomLabel?: boolean): Promise<number | null> => {
    if (!feedId) return null;
    if (uploadInFlightRef.current) return null;
    uploadInFlightRef.current = true;
    try {
      return await runUpload(approvedOnly, addCustomLabel);
    } finally {
      uploadInFlightRef.current = false;
    }
  };

  const runUpload = async (approvedOnly: boolean, addCustomLabel?: boolean): Promise<number | null> => {
    if (!feedId) return null;

    // GMC-access guard (defense-in-depth — UI buttons are disabled).
    // Mirrors the server-side guard in enqueue-upload-batches (returns 409 GMC_ACCESS_REVOKED).
    if (isMcAccessRevoked) {
      setShowUploadConfirmDialog(false);
      toast({
        title: i18n.t('dashboard:toasts.mcRevokedTitle'),
        description: revokedMcName
          ? i18n.t('dashboard:toasts.uploadMcRevokedNamed', { name: revokedMcName })
          : i18n.t('dashboard:toasts.uploadMcRevoked'),
        variant: 'destructive',
      });
      return null;
    }

    setShowUploadConfirmDialog(false);
    setUploadSubmitting(true);

    try {
      const body: Record<string, unknown> = { feed_id: feedId, approved_only: approvedOnly };
      if (addCustomLabel !== undefined) {
        body.custom_label = addCustomLabel;
      }
      if (uploadSelection?.productIds?.length) {
        body.product_ids = uploadSelection.productIds;
      } else if (uploadSelection?.filters && Object.keys(uploadSelection.filters).length > 0) {
        body.filters = uploadSelection.filters;
      }

      const res = await supabase.functions.invoke('enqueue-upload-batches', { body });

      // Guard-Codes zuerst pruefen: extractReauthError liest den Response-Body und
      // macht ihn danach unklonbar.
      const guardBody = res.error ? await readEdgeErrorBody(res) : null;
      if (isUploadGuardCode(guardBody?.error)) {
        toast({
          title: i18n.t('dashboard:toasts.uploadNotStarted'),
          description: guardBody.error === 'EXPERIMENT_CONTROL_BLOCKED'
            ? i18n.t('dashboard:toasts.uploadControlBlocked')
            : i18n.t('dashboard:toasts.uploadGuardUnavailable'),
          variant: 'destructive',
        });
        return null;
      }

      const reauthError = await extractReauthError(res);
      if (reauthError) {
        setUploadSubmitting(false);
        // Retry with the same mode — uploadSelection survives in state.
        // Bewusst `null`: der Wiederholungslauf haengt am Reauth-Callback, nicht
        // an diesem Aufruf. Sein Ergebnis erreicht den urspruenglichen Aufrufer
        // nicht, also darf hier auch kein Erfolg gemeldet werden.
        triggerReauth(reauthError, () => handleUpload(approvedOnly, addCustomLabel));
        return null;
      }

      if (res.error) throw res.error;
      if (res.data?.error) throw new Error(res.data.error);

      // Herkunft VOR dem State-Update festhalten: der Render, den
      // setActiveUploadJob ausloest, liest das Set bereits.
      startedJobIdsRef.current.add(res.data.job_id);
      setActiveUploadJob({
        id: res.data.job_id,
        status: 'queued',
        total_products: res.data.total_products,
        uploaded_products: 0,
        failed_products: 0,
        queued_batches: res.data.total_batches,
        processed_batches: 0,
      });
      setUploadDialogDismissed(false);
      setUploadSelection(null);
      setScopedCounts(null);

      invalidate();
      // `count` numerisch (Pluralwahl), `formatted` fuer die Anzeige — ein
      // String-count schaltet die i18next-Pluralformen ab ("1 Produkte").
      toast({ title: i18n.t('dashboard:toasts.uploadStarted'), description: i18n.t('dashboard:toasts.uploadQueued', { count: res.data.total_products, formatted: res.data.total_products.toLocaleString() }) });

      // Der Server hat Produkte der Kontrollgruppe aussortiert. Das ist gewolltes
      // Verhalten, kein Fehler, aber ohne Hinweis sieht ein halbierter Upload
      // aus wie ein voller Erfolg.
      const skippedControl = typeof res.data?.skipped_control === 'number' ? res.data.skipped_control : 0;
      if (skippedControl > 0) {
        toast({
          tone: 'warning',
          title: i18n.t('dashboard:toasts.uploadControlHeldBackTitle'),
          description: i18n.t('dashboard:toasts.uploadControlHeldBack', { count: skippedControl, formatted: skippedControl.toLocaleString() }),
        });
      }

      return typeof res.data?.total_products === 'number' ? res.data.total_products : 0;
    } catch (error) {
      Sentry.captureException(error, { tags: { feature: 'upload', action: 'start' } });
      toast({ title: i18n.t('dashboard:toasts.uploadFailed'), description: error instanceof Error ? error.message : i18n.t('dashboard:toasts.uploadError'), variant: 'destructive' });
      return null;
    } finally {
      setUploadSubmitting(false);
    }
  };

  // Cancel upload
  const handleCancelUpload = async () => {
    if (!activeUploadJob) return;
    cancelledUploadRef.current = true;
    setCancellingUpload(true);
    try {
      await cancelJob(activeUploadJob.id);
      setActiveUploadJob(null);
      toast({ title: i18n.t('dashboard:toasts.uploadCancelled') });
    } catch (error) {
      Sentry.captureException(error, { tags: { feature: 'upload', action: 'cancel' } });
      // Der Abbruch ist gescheitert, der Upload laeuft weiter: seine
      // Abschluss-Meldung darf nicht am stehenden Merker verloren gehen.
      cancelledUploadRef.current = false;
      toast({ title: i18n.t('common:status.error'), description: i18n.t('dashboard:toasts.cancelUploadFailed'), variant: 'destructive' });
    } finally {
      setCancellingUpload(false);
    }
  };

  // Konvergenz 6, Befund 2: der Completion-Effekt setzt completedUploadJob
  // auch fuer status='failed' (process-upload-queue markiert 0 hochgeladene
  // als 'failed'; der Watchdog killt Teilstaende). Bedingungslos folgte auf
  // den ehrlichen Fehlerdialog die Sparkles-Zeremonie mit "0 Produkte
  // hochgeladen!" und "jetzt live". Deshalb: Terminal-Status VOR dem Nullen
  // lesen und die Zeremonie nur bei 'completed' oeffnen — bei 'failed' war
  // der Fehlerdialog die Wahrheit, ein Jubel-Folgedialog verkehrte sie ins
  // Gegenteil.
  const handleCloseUploadDialog = () => {
    const finalStatus = completedUploadJob?.status;
    setCompletedUploadJob(null);
    if (finalStatus === 'completed') {
      setShowUploadSuccessDialog(true);
    }
  };

  // X/Escape waehrend des Laufs: Dialog zu, Upload laeuft weiter — exakt das,
  // was die backgroundNote-Copy verspricht. Kein Abbruch, kein Purge.
  const dismissUploadDialog = () => setUploadDialogDismissed(true);

  // Herkunft des gerade angezeigten Laufs (Befund 2): true nur, wenn der
  // aktive bzw. fertige Job in dieser Hook-Instanz gestartet wurde. Die
  // Konsumenten gaten damit das Auto-Oeffnen des Fortschrittsdialogs.
  const displayedUploadJob = completedUploadJob ?? activeUploadJob;
  const uploadStartedHere = !!displayedUploadJob && startedJobIdsRef.current.has(displayedUploadJob.id);

  return {
    activeUploadJob,
    completedUploadJob,
    isUploading,
    uploadStartedHere,
    uploadDialogDismissed,
    dismissUploadDialog,
    uploadSubmitting,
    cancellingUpload,
    showUploadSuccessDialog,
    setShowUploadSuccessDialog,
    showUploadConfirmDialog,
    setShowUploadConfirmDialog,
    lastUploadStats,
    isScopedUpload: !!uploadSelection,
    scopedCounts,
    countsLoading,
    handleUpload,
    handleUploadButtonClick,
    handleCancelUpload,
    handleCloseUploadDialog,
  };
}
